import type { IconName } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";
import type { CustomFieldDef } from "#app/components/custom-fields/types";

// ---------------------------------------------------------------------------
// Operators
// ---------------------------------------------------------------------------

export const SCALAR_OPERATORS = [
  "eq",
  "ne",
  "contains",
  "notContains",
  "startsWith",
  "endsWith",
  "gt",
  "gte",
  "lt",
  "lte",
] as const;

export type ScalarOperator = (typeof SCALAR_OPERATORS)[number];

export const ARRAY_OPERATORS = [
  "in",
  "nin",
  "has",
  "hasSome",
  "hasNone",
  "hasEvery",
] as const;

export type ArrayOperator = (typeof ARRAY_OPERATORS)[number];

export const NULL_OPERATORS = [
  "isNull",
  "isNotNull",
  "isEmpty",
  "isNotEmpty",
] as const;

export type NullOperator = (typeof NULL_OPERATORS)[number];

/** Every operator a saved filter can carry — the runtime list a persisted
 *  `FilterState` is validated against. */
export const FILTER_OPERATORS = [
  ...SCALAR_OPERATORS,
  ...ARRAY_OPERATORS,
  ...NULL_OPERATORS,
] as const;

export type FilterOperator = (typeof FILTER_OPERATORS)[number];

export type OperatorInput = "single" | "multiple" | "none";

export type OperatorDef = {
  value: FilterOperator;
  label: string;
  input: OperatorInput;
};

// ---------------------------------------------------------------------------
// Field types (determines which value input component renders)
// ---------------------------------------------------------------------------

export type FilterFieldType =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "email"
  | "select"
  | "multi-select"
  | "entity-user"
  | "entity-team"
  | "entity-assignee"
  | "role"
  | "workflow"
  | "stage"
  | "issue-type"
  | "age-status"
  | "workflow-stage"
  | "last-updated"
  | "more-options"
  | "custom-field";

// ---------------------------------------------------------------------------
// Field definitions
// ---------------------------------------------------------------------------

export type FilterFieldDef = {
  field: string;
  label: string;
  type: FilterFieldType;
  operators: OperatorDef[];
  /** Set on `custom-field` defs only — the workspace field the value input
   *  renders for (option pickers need config.options, member needs the
   *  member picker, etc.). */
  customField?: CustomFieldDef;
};

export type QuickFilterDef = Omittable<{
  field: string;
  label: string;
  type: FilterFieldType;
  operator: FilterOperator;
  icon?: IconName;
  /** The run the row sits in on the Simple panel. Rows carrying the same
   *  group collect together, in config order, between hairlines; an ungrouped
   *  row stands in its own run. Seven rows in one undifferentiated column is
   *  a list nobody scans. */
  group?: string;
  /** What the row reads while nothing is applied, when the type's default
   *  ("Any workflow") would misname a relabeled row. */
  unfilteredFace?: string;
}>;

// ---------------------------------------------------------------------------
// Filter state (the data model)
// ---------------------------------------------------------------------------

export type FilterValue = {
  field: string;
  operator: FilterOperator;
  value: unknown;
};

export type FilterBlock = {
  logic: "AND" | "OR";
  filters: FilterValue[];
};

export type FilterState = {
  blocks: FilterBlock[];
  logic: "AND" | "OR";
};

// ---------------------------------------------------------------------------
// Per-entity configuration
// ---------------------------------------------------------------------------

export type FilterConfig = Omittable<{
  category: string;
  fields: Record<string, FilterFieldDef>;
  quickFilters?: QuickFilterDef[];
  searchableFields?: string[];
}>;

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export type FilterMode = "simple" | "advanced";

/**
 * Which filter layer a quick filter's value lives in, by field. The toolbar
 * panel edits a session pool, but a field can be routed to the VIEW's own
 * layer (the Assignee filter: what a screen is filtered to by person is
 * saved with the view) — the panel's rows, its search and any chip that
 * doubles as a door for the field all read and write through this, so a
 * routed field has ONE value however it is reached. Defaults to the panel's
 * own `filters`.
 */
export type QuickFilterLayerFor = (field: string) => UseFiltersResult;

export type UseFiltersResult = {
  activeMode: FilterMode;
  setActiveMode: (mode: FilterMode) => void;
  filterState: FilterState;
  advancedState: FilterState;
  quickValues: Record<string, unknown>;
  setQuickValue: (field: string, value: unknown) => void;
  addBlock: () => void;
  removeBlock: (index: number) => void;
  setBlockLogic: (index: number, logic: "AND" | "OR") => void;
  setMainLogic: (logic: "AND" | "OR") => void;
  addFilter: (blockIndex: number) => void;
  updateFilter: (
    blockIndex: number,
    filterIndex: number,
    update: Partial<FilterValue>
  ) => void;
  removeFilter: (blockIndex: number, filterIndex: number) => void;
  clearAll: () => void;
  /** Replace BOTH editors' states at once — how a saved filter is applied.
   *  Stored states pass through the same shape migration a seed does. */
  applyState: (simpleState: FilterState, advancedState: FilterState) => void;
  activeFilterCount: number;
  simpleFilterCount: number;
  advancedFilterCount: number;
  where: Record<string, unknown> | undefined;
};
